import _toConsumableArray from "/var/www/mol_finder/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Use of this source code is governed by an MIT-style
 * license that can be found in the LICENSE file or at
 * https://opensource.org/licenses/MIT.
 * =============================================================================
 */
import { countParamsInWeights } from './variable_utils';
/**
 * Print the summary of a LayersModel object.
 *
 * @param model tf.LayersModel instance.
 * @param lineLength Total length of printed lines. Set this to adapt to the
 *   display to different terminal or console sizes.
 * @param positions Relative or absolute positions of log elements in each
 *   line. Each number corresponds to right-most (i.e., ending) position of a
 *   column.
 *   If not provided, defaults to `[0.45, 0.85, 1]` for sequential-like
 *   models and `[0.33, 0.55, 0.67, 1]` for non-sequential like models.
 * @param printFn Print function to use.
 *   It will be called on each line of the summary. You can provide a custom
 *   function in order to capture the string summary. Defaults to `console.log`.
 */

export function printSummary(model, lineLength, positions) {
  var printFn = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : console.log;
  var sequentialLike = isModelSequentialLike(model); // Header names for different log elements.

  var toDisplay = ['Layer (type)', 'Input Shape', 'Output shape', 'Param #'];

  if (sequentialLike) {
    lineLength = lineLength || 90;
    positions = positions || [0.32, 0.61, 0.89, 1];
  } else {
    lineLength = lineLength || 115;
    positions = positions || [0.24, 0.48, 0.70, 0.80, 1]; // Header names for different log elements.
  }

  if (positions[positions.length - 1] <= 1) {
    // `positions` is relative. Convert it to absolute positioning.
    positions = positions.map(function (p) {
      return Math.floor(lineLength * p);
    });
  }

  var relevantNodes;

  if (!sequentialLike) {
    toDisplay.push('Receives inputs');
    relevantNodes = [];

    for (var depth in model.nodesByDepth) {
      var _relevantNodes;

      (_relevantNodes = relevantNodes).push.apply(_relevantNodes, _toConsumableArray(model.nodesByDepth[depth]));
    }
  }

  printFn('_'.repeat(lineLength));
  printRow(toDisplay, positions, printFn);
  printFn('='.repeat(lineLength));
  var layers = model.layers;

  for (var i = 0; i < layers.length; ++i) {
    if (sequentialLike) {
      printLayerSummary(layers[i], positions, printFn);
    } else {
      printLayerSummaryWithConnections(layers[i], positions, relevantNodes, printFn);
    }

    printFn((i === layers.length - 1 ? '=' : '_').repeat(lineLength));
  } // tslint:disable-next-line:no-any


  model.checkTrainableWeightsConsistency();
  var trainableCount = countTrainableParams(model);
  var nonTrainableCount = countParamsInWeights(model.nonTrainableWeights);
  printFn("Total params: ".concat(trainableCount + nonTrainableCount));
  printFn("Trainable params: ".concat(trainableCount));
  printFn("Non-trainable params: ".concat(nonTrainableCount));
  printFn('_'.repeat(lineLength));
}

function countTrainableParams(model) {
  var trainableCount; // tslint:disable:no-any

  if (model.collectedTrainableWeights != null) {
    trainableCount = countParamsInWeights(model.collectedTrainableWeights);
  } else {
    trainableCount = countParamsInWeights(model.trainableWeights);
  } // tslint:enable:no-any


  return trainableCount;
}

function isModelSequentialLike(model) {
  var sequentialLike = true;
  var nodesByDepth = [];
  var nodes = [];

  for (var depth in model.nodesByDepth) {
    nodesByDepth.push(model.nodesByDepth[depth]);
  }

  for (var _i = 0, _nodesByDepth = nodesByDepth; _i < _nodesByDepth.length; _i++) {
    var depthNodes = _nodesByDepth[_i];

    if (depthNodes.length > 1 || depthNodes.length === 1 && depthNodes[0].inboundLayers.length > 1) {
      sequentialLike = false;
      break;
    }

    nodes.push.apply(nodes, _toConsumableArray(depthNodes));
  }

  if (sequentialLike) {
    // Search for shared layers.
    var _iterator = _createForOfIteratorHelper(model.layers),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var layer = _step.value;
        var flag = false;

        var _iterator2 = _createForOfIteratorHelper(layer.inboundNodes),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var node = _step2.value;

            if (nodes.indexOf(node) !== -1) {
              if (flag) {
                sequentialLike = false;
                break;
              } else {
                flag = true;
              }
            }
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }

        if (!sequentialLike) {
          break;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  return sequentialLike;
}

function printRow(fields, positions) {
  var printFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : console.log;
  var line = '';

  for (var i = 0; i < fields.length; ++i) {
    if (i > 0) {
      line = line.slice(0, line.length - 1) + ' ';
    }

    line += fields[i];
    line = line.slice(0, positions[i]);
    line += ' '.repeat(positions[i] - line.length);
  }

  printFn(line);
}
/**
 * Prints a summary for a single Layer, without connectivity information.
 *
 * @param layer: Layer instance to print.
 */


function printLayerSummary(layer, positions, // tslint:disable-next-line:no-any
printFn) {
  var outputShape;
  var inputShape;

  try {
    inputShape = layer.inboundNodes.map(function (x) {
      return JSON.stringify(x.inputShapes);
    }).join(',');
  } catch (err) {
    inputShape = 'multiple';
  }

  try {
    outputShape = JSON.stringify(layer.outputShape);
  } catch (err) {
    outputShape = 'multiple';
  }

  var name = layer.name;
  var className = layer.getClassName();
  var fields = ["".concat(name, " (").concat(className, ")"), inputShape, outputShape, layer.countParams().toString()];
  printRow(fields, positions, printFn);
}
/**
 * Prints a summary for a single Layer, with connectivity information.
 */


function printLayerSummaryWithConnections(layer, positions, relevantNodes, // tslint:disable-next-line:no-any
printFn) {
  var outputShape;
  var inputShape;

  try {
    inputShape = layer.inboundNodes.map(function (x) {
      return JSON.stringify(x.inputShapes);
    }).join(',');
  } catch (err) {
    inputShape = 'multiple';
  }

  try {
    outputShape = JSON.stringify(layer.outputShape);
  } catch (err) {
    outputShape = 'multiple';
  }

  var connections = [];

  var _iterator3 = _createForOfIteratorHelper(layer.inboundNodes),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var node = _step3.value;

      if (relevantNodes != null && relevantNodes.length > 0 && relevantNodes.indexOf(node) === -1) {
        continue;
      }

      for (var _i2 = 0; _i2 < node.inboundLayers.length; ++_i2) {
        var inboundLayer = node.inboundLayers[_i2].name;
        var inboundLayerIndex = node.nodeIndices[_i2];
        var inboundTensorIndex = node.tensorIndices[_i2];
        connections.push("".concat(inboundLayer, "[").concat(inboundLayerIndex, "][").concat(inboundTensorIndex, "]"));
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  var name = layer.name;
  var className = layer.getClassName();
  var firstConnection = connections.length === 0 ? '' : connections[0];
  var fields = ["".concat(name, " (").concat(className, ")"), inputShape, outputShape, layer.countParams().toString(), firstConnection];
  printRow(fields, positions, printFn);

  for (var i = 1; i < connections.length; ++i) {
    printRow(['', '', '', '', connections[i]], positions, printFn);
  }
}